import { createContext, ReactNode, useContext, useState } from 'react';
import { refProjects } from '../data/data';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Feature, Project } from '../types';

interface AppContextType {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  drawerMobileOpen: boolean;
  setDrawerMobileOpen: (open: boolean) => void;
  drawerWidth: number;
  setDrawerWidth: (width: number) => void;
  isDarkMode: boolean;
  setIsDarkMode: (mode: boolean) => void;
  app: Apps;
  setApp: (app: Apps) => void;
  role: string;
  setNewRole: (role: string) => void;
  completed: any;
  setCompleted: (completed: any) => void;
  activeStep: number;
  setActiveStep: (step: number) => void;
  referenceProjects: Project[];
  setReferenceProjects: (projects: Project[]) => void;
  modified: boolean;
  setModified: (modified: boolean) => void;
}

interface AppContextProviderProps {
  children: ReactNode;
}

export enum Apps {
  SoilingMitigation = 'Soiling Mitigation',
  UserManager = 'User Manager',
  Profile = 'Profile',
}

// In PROD show only the Assessment Role.
export const Roles = [
  {
    label: 'Assessment',
    value: 'assessment',
    icon: <AssessmentIcon />,
  },
  {
    label: 'Mitigation Strategy',
    value: 'mitigation_strategy',
    icon: <CompareArrowsIcon />,
  },
  {
    label: 'Ex-Post Analysis',
    value: 'ex_post_analysis',
    icon: <CompareArrowsIcon />,
  },
  {
    label: 'Operational Strategy',
    value: 'operational_strategy',
    icon: <CompareArrowsIcon />,
  },
];

export const features: Feature[] = [
  {
    name: 'Soiling Risk Assessment',
    img: 'assessment.png',
    role: 'assessment',
    path: '/project',
    description: 'In development phase, this process will give an indication on the soiling risk associated to a specific location.',
  },
  {
    name: 'Mitigation Strategy',
    img: 'optimization.png',
    role: 'mitigation_strategy',
    path: '/project',
    description:
      'In development phase, this process will suggest a soiling mitigation strategy associated to a specific location and for a (pre-)designed PV plant',
  },
  {
    name: 'Ex-Post Analysis',
    img: 'optimization.png',
    role: 'ex_post_analysis',
    path: '/project',
    description:
      'For plants in operation, this process allows to analyze a past period and compare the implemented mitigation strategy with what could have been optimal.',
  },
  {
    name: 'Operational Strategy',
    img: 'optimization.png',
    role: 'operational_strategy',
    path: '/project',
    description: 'For plants in operation, this process will suggest a soiling mitigation strategy based on sensor data',
  },
];

export const locale: string = 'en-GB'; // 'default'

const AppContext = createContext<AppContextType | undefined>(undefined);

const AppContextProvider: React.FC<AppContextProviderProps> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerMobileOpen, setDrawerMobileOpen] = useState(false);
  const [modified, setModified] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(360);
  const [app, setApp] = useState(Apps.SoilingMitigation);
  const [role, setRole] = useState(() => {
    const storedRole = localStorage.getItem('role');
    return storedRole ? storedRole : 'assessment';
  });
  const [referenceProjects, setReferenceProjects] = useState(refProjects);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const storedTheme = localStorage.getItem('isDarkTheme');
    return storedTheme ? JSON.parse(storedTheme) : false;
  });
  const [completed, setCompleted] = useState<{
    [k: number]: boolean;
  }>({});
  const [activeStep, setActiveStep] = useState(0);

  const setNewRole = (newRole: string) => {
    setCompleted({});
    localStorage.setItem('role', newRole);
    setRole(newRole);
    setActiveStep(0);
  };
  return (
    <AppContext.Provider
      value={{
        drawerOpen,
        setDrawerOpen,
        drawerMobileOpen,
        setDrawerMobileOpen,
        drawerWidth,
        setDrawerWidth,
        isDarkMode,
        setIsDarkMode,
        app,
        setApp,
        role,
        setNewRole,
        completed,
        setCompleted,
        activeStep,
        setActiveStep,
        referenceProjects,
        setReferenceProjects,
        modified,
        setModified,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }
  return context;
};

export default AppContextProvider;
